import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { DollarSign, TrendingUp, Users, Percent, Award, Zap } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { hydrateBenchmarks, TYPE_BY_VALUE } from "../lib/propertyTypes";
import { DuplicateClaimsCard, BenchmarksCard } from "../components/admin/AdminRevenueSections";

// TYPE_BY_VALUE is re-imported so the main dashboard retains its old prop-type
// shape utility (BenchmarkRow moved into AdminRevenueSections).
void TYPE_BY_VALUE;

const currency = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const currency2 = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;

const TIER_COLORS = {
  starter: "#94a3b8",
  pro: "#0f172a",
  enterprise: "#f59e0b",
  none: "#e2e8f0",
};

// Pseudo-random but stable palette for per-month bar coloring on the
// Revenue Dashboard charts. The hash is deterministic per month label so
// the bars don't flicker on re-render.
const MONTH_PALETTE = [
  "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#22C55E",
  "#06B6D4", "#A855F7",
];
const monthColor = (label) => {
  let h = 0;
  for (let i = 0; i < String(label || "").length; i++) {
    h = (h * 31 + label.charCodeAt(i)) >>> 0;
  }
  return MONTH_PALETTE[h % MONTH_PALETTE.length];
};

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [onboarding, setOnboarding] = useState(null);

  const loadBenchmarks = () => {
    api.get("/admin/benchmarks").then((r) => setBenchmarks(r.data)).catch((e) => console.warn("benchmarks load failed", e));
  };

  const loadDuplicates = (includeResolved = showResolved) => {
    api.get("/admin/duplicate-claims", { params: { include_resolved: includeResolved } })
      .then((r) => setDuplicates(r.data))
      .catch(() => setDuplicates(null));
  };

  useEffect(() => {
    api.get("/admin/revenue").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/admin/attribution").then((r) => setAttribution(r.data)).catch(() => setAttribution(null));
    api.get("/admin/onboarding-stats").then((r) => setOnboarding(r.data)).catch(() => setOnboarding(null));
    loadDuplicates(false);
    loadBenchmarks();
  }, []);

  useEffect(() => {
    loadDuplicates(showResolved);
  }, [showResolved]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveCollision = async (placeId) => {
    const notes = window.prompt("Resolution notes (optional):\n\nDescribe what you investigated so future admins have context.", "") || "";
    try {
      await api.post(`/admin/duplicate-claims/${encodeURIComponent(placeId)}/resolve`, { notes });
      toast.success("Collision marked as resolved");
      loadDuplicates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resolve");
    }
  };

  const unresolveCollision = async (placeId) => {
    if (!window.confirm("Reopen this collision for investigation?")) return;
    try {
      await api.delete(`/admin/duplicate-claims/${encodeURIComponent(placeId)}/resolve`);
      toast.success("Collision re-opened");
      loadDuplicates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not un-resolve");
    }
  };

  const saveBenchmark = async (type, min, max) => {
    try {
      await api.put(`/admin/benchmarks/${type}`, { min: Number(min), max: Number(max) });
      toast.success(`${TYPE_BY_VALUE[type]?.label || type} override saved`);
      await hydrateBenchmarks(api);
      loadBenchmarks();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const resetBenchmark = async (type) => {
    try {
      await api.delete(`/admin/benchmarks/${type}`);
      toast.success(`${TYPE_BY_VALUE[type]?.label || type} reset to default`);
      await hydrateBenchmarks(api);
      loadBenchmarks();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
  };

  if (data === null) return <DashboardShell><div className="p-8 text-slate-500">Loading platform KPIs…</div></DashboardShell>;
  if (data === false) return <DashboardShell><div className="p-8 text-red-600">Admin access required.</div></DashboardShell>;

  const tierPie = Object.entries(data.tier_counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k, value: v, fill: TIER_COLORS[k] }));

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="overline text-slate-500">Platform</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Revenue Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Platform-wide SaaS metrics, subscription mix, and top accounts.</p>
          </div>
        </div>

        {/* Primary KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 stagger-in">
          <KpiCard label="MRR" value={currency2(data.mrr)} icon={DollarSign} testid="kpi-mrr" tone="green" />
          <KpiCard label="ARR" value={currency(data.arr)} icon={TrendingUp} testid="kpi-arr" tone="blue" />
          <KpiCard label="Active Subscriptions" value={data.active_subscriptions} icon={Users} testid="kpi-active" tone="purple" />
          <KpiCard label="ARPU" value={currency2(data.arpu)} icon={Award} testid="kpi-arpu" tone="red" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KpiCard label="Total Platform Users" value={data.total_investors} small testid="kpi-investors" tone="yellow" />
          <KpiCard label="Churned (30d)" value={data.churned_this_month} small testid="kpi-churned" tone="brown" />
          <KpiCard label="Churn Rate" value={`${data.churn_rate}%`} small icon={Percent} testid="kpi-churn-rate" tone="red" />
          <KpiCard label="Gross Volume (rent)" value={currency(data.total_rent_processed)} small testid="kpi-rent-volume" />
        </div>

        {/* Trend chart + tier mix */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
            <div className="overline text-slate-500">6-Month Trend</div>
            <h3 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Collected Revenue</h3>
            <div className="h-[300px] mt-4">
              <ResponsiveContainer>
                <BarChart data={data.trend}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="subscription" name="Subscription revenue">
                    {data.trend.map((m) => (
                      <Cell key={`sub-${m.month}`} fill={monthColor(m.month)} />
                    ))}
                  </Bar>
                  <Bar dataKey="rent" name="Rent processed">
                    {data.trend.map((m) => (
                      <Cell key={`rent-${m.month}`} fill={monthColor(`${m.month}-r`)} fillOpacity={0.55} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="overline text-slate-500">Mix</div>
            <h3 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Subscriptions by Tier</h3>
            <div className="h-[220px] mt-4">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={tierPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                    {tierPie.map((e) => <Cell key={e.name} fill={e.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 space-y-1 text-sm">
              {["enterprise", "pro", "starter", "none"].map((tier) => (
                <li key={tier} className="flex items-center justify-between" data-testid={`tier-${tier}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: TIER_COLORS[tier] }}></span>
                    <span className="overline text-slate-600">{tier}</span>
                  </span>
                  <span className="font-mono text-slate-900 font-bold">{data.tier_counts[tier]}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Lead attribution */}
        {attribution && (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mb-6" data-testid="attribution-card">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-slate-500" />
                <div>
                  <div className="overline text-slate-500">Growth</div>
                  <div className="text-sm font-semibold text-slate-900">Lead attribution & conversion</div>
                </div>
              </div>
              <div className="text-xs text-slate-500 font-mono">
                {attribution.total_signups} signups · {attribution.total_activated} activated · {attribution.overall_conversion}% overall
              </div>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-[240px]">
                  <ResponsiveContainer>
                    <BarChart data={attribution.monthly}>
                      <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                      <YAxis stroke="#64748b" fontSize={11} />
                      <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #e2e8f0", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {attribution.rows.slice(0, 5).map((r, idx) => (
                        <Bar key={r.source} dataKey={r.source} stackId="a" name={r.source}>
                          {attribution.monthly.map((m) => (
                            <Cell
                              key={`${r.source}-${m.month}`}
                              fill={monthColor(`${m.month}-${idx}`)}
                            />
                          ))}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="attribution-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left">
                        <Th>Source</Th>
                        <Th className="text-right">Signups</Th>
                        <Th className="text-right">Activated</Th>
                        <Th className="text-right">Conv.</Th>
                        <Th className="text-right">MRR</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {attribution.rows.map((r) => (
                        <tr key={r.source} className="border-b border-slate-100" data-testid={`attribution-${r.source}`}>
                          <Td>
                            <span className="overline text-slate-500">{r.source}</span>
                          </Td>
                          <Td className="text-right font-mono">{r.signups}</Td>
                          <Td className="text-right font-mono">{r.activated}</Td>
                          <Td className={`text-right font-mono font-bold ${r.conversion_rate >= 20 ? "text-green-600" : r.conversion_rate >= 10 ? "text-amber-600" : "text-slate-500"}`}>
                            {r.conversion_rate}%
                          </Td>
                          <Td className="text-right font-mono">{currency2(r.mrr)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        <OnboardingFunnelCard onboarding={onboarding} />
        <DuplicateClaimsCard
          duplicates={duplicates}
          showResolved={showResolved}
          onToggleResolved={setShowResolved}
          onResolve={resolveCollision}
          onUnresolve={unresolveCollision}
        />
        <BenchmarksCard benchmarks={benchmarks} onSave={saveBenchmark} onReset={resetBenchmark} />
      </div>
    </DashboardShell>
  );
}


function TopInvestorRow() {
  // Kept as a no-op placeholder because the "Top-earning platform users"
  // section was removed from the dashboard. Left exported so any future
  // re-add can pick the helper back up; not currently rendered.
  return null;
}
void TopInvestorRow;


function KpiCard({ label, value, icon: Icon, testid, small, negative, tone }) {
  // Tonal palette for the Revenue Dashboard KPIs.
  // `tone` overrides the default white-on-slate-900 icon chip + body text.
  const tones = {
    green:  { box: "bg-emerald-50 border-emerald-200", label: "text-emerald-700", value: "text-emerald-700", chip: "bg-emerald-600" },
    blue:   { box: "bg-sky-50 border-sky-200",         label: "text-sky-700",     value: "text-sky-700",     chip: "bg-sky-600" },
    purple: { box: "bg-purple-50 border-purple-200",   label: "text-purple-700",  value: "text-purple-700",  chip: "bg-purple-600" },
    red:    { box: "bg-red-50 border-red-200",         label: "text-red-700",     value: "text-red-700",     chip: "bg-red-600" },
    yellow: { box: "bg-amber-50 border-amber-200",     label: "text-amber-800",   value: "text-amber-900",   chip: "bg-amber-600" },
    brown:  { box: "bg-amber-100 border-amber-300",    label: "text-amber-900",   value: "text-amber-900",   chip: "bg-amber-900" },
  };
  const t = tones[tone];
  const boxCls = t ? `${t.box} border` : "bg-white border border-slate-200";
  const labelCls = t ? `overline ${t.label}` : "overline text-slate-500";
  const valueCls = t ? t.value : (negative ? "text-red-600" : "text-slate-900");
  const chipCls = t ? t.chip : "bg-slate-900";
  return (
    <div data-testid={testid} className={`${boxCls} rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={labelCls}>{label}</div>
          <div className={`mt-3 ${small ? "text-2xl" : "text-3xl"} font-bold tracking-tight font-mono ${valueCls}`}>
            {value}
          </div>
        </div>
        {Icon && (
          <div className={`w-9 h-9 ${chipCls} text-white flex items-center justify-center rounded-sm`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }

function OnboardingFunnelCard({ onboarding }) {
  if (!onboarding) return null;
  const rate = Math.round((onboarding.completion_rate || 0) * 100);
  const chartData = (onboarding.by_month || []).map((m) => ({
    month: m.month.slice(5),  // show just MM
    signups: m.signups,
    completed: m.completed,
    skipped: Math.max(0, m.signups - m.completed),
  }));
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 mb-8" data-testid="onboarding-funnel-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="overline text-slate-500">Onboarding</div>
          <h3 className="text-sm font-bold text-slate-900">Product-tour funnel</h3>
          <div className="text-xs text-slate-500 mt-1 max-w-md">
            Investors who complete the interactive tour are 3× more likely to reach their first rent collection. Low completion = a signal to intervene.
          </div>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold tabular-nums ${rate >= 70 ? "text-emerald-600" : rate >= 40 ? "text-amber-600" : "text-red-600"}`}>
            {rate}%
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            {onboarding.completed}/{onboarding.total} completed
          </div>
        </div>
      </div>

      {/* 3-tile summary */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <FunnelTile label="Total signups" value={onboarding.total} tone="slate" />
        <FunnelTile label="Tour completed" value={onboarding.completed} tone="emerald" />
        <FunnelTile label="Skipped / pending" value={onboarding.skipped_or_pending} tone="amber" />
      </div>

      {/* Stacked bar by month */}
      {chartData.length > 0 && (
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 2 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="completed" stackId="a" fill="#059669" name="Completed tour" />
              <Bar dataKey="skipped" stackId="a" fill="#f59e0b" name="Skipped / pending" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function FunnelTile({ label, value, tone }) {
  const toneCls = tone === "emerald"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`rounded-sm border p-3 ${toneCls}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}
